import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { notifications as notifApi } from "../lib/api";
import { Bell, Loader2 } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuTrigger,
} from "./ui/dropdown-menu";

const timeAgo = (iso) => {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "à l'instant";
  if (s < 3600) return `il y a ${Math.floor(s / 60)} min`;
  if (s < 86400) return `il y a ${Math.floor(s / 3600)} h`;
  return `il y a ${Math.floor(s / 86400)} j`;
};

const routeFor = (n) => {
  const d = n.data || {};
  if (d.conversationId) return `/messages?c=${d.conversationId}`;
  if (d.eventId) return `/game/${d.eventId}`;
  if (d.tournamentId) return `/player/event/tournament/${d.tournamentId}`;
  return null;
};

export const NotificationBell = () => {
  const navigate = useNavigate();
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const timer = useRef(null);

  const fetchCount = useCallback(async () => {
    try { const r = await notifApi.unreadCount(); setCount(r.data?.count ?? r.data?.unread ?? 0); } catch { /* silent */ }
  }, []);

  useEffect(() => {
    fetchCount();
    timer.current = setInterval(fetchCount, 20000);
    return () => clearInterval(timer.current);
  }, [fetchCount]);

  const onOpenChange = async (v) => {
    setOpen(v);
    if (v) {
      setLoading(true);
      try {
        const r = await notifApi.list();
        setItems(Array.isArray(r.data) ? r.data : (r.data?.notifications || []));
        await notifApi.markAllRead();
        setCount(0);
      } catch { /* silent */ }
      finally { setLoading(false); }
    }
  };

  const openItem = (n) => {
    const r = routeFor(n);
    setOpen(false);
    if (r) navigate(r);
  };

  return (
    <DropdownMenu open={open} onOpenChange={onOpenChange}>
      <DropdownMenuTrigger asChild>
        <button className="relative p-2 rounded-md text-[#999] hover:text-white hover:bg-[#252525] transition-colors" data-testid="notif-bell-btn" aria-label="Notifications">
          <Bell className="w-5 h-5" />
          {count > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#FF6B35] text-white text-[10px] font-bold flex items-center justify-center" data-testid="notif-badge">
              {count > 99 ? "99+" : count}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-[#1a1a1a] border-[#333] w-80 max-h-[70vh] overflow-y-auto p-0" data-testid="notif-dropdown">
        <div className="px-4 py-3 border-b border-[#333] sticky top-0 bg-[#1a1a1a]">
          <p className="text-white font-semibold text-sm">Notifications</p>
        </div>
        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 text-[#FF6B35] animate-spin" /></div>
        ) : items.length === 0 ? (
          <div className="px-4 py-8 text-center text-[#666] text-sm">Aucune notification</div>
        ) : (
          <div className="divide-y divide-[#2a2a2a]">
            {items.slice(0, 30).map((n) => (
              <button key={n.id} onClick={() => openItem(n)} className={`w-full text-left px-4 py-3 hover:bg-[#252525] transition-colors ${!n.read ? "bg-[#FF6B35]/5" : ""}`} data-testid="notif-item">
                <p className="text-white text-sm font-medium">{n.title}</p>
                <p className="text-[#B0B0B0] text-xs mt-0.5 line-clamp-2">{n.body || n.message}</p>
                <p className="text-[#555] text-[10px] mt-1">{n.created_at ? timeAgo(n.created_at) : ""}</p>
              </button>
            ))}
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default NotificationBell;
